<ion-header>
  <ion-toolbar color="primary">
    <ion-title>
      <ion-icon name="help-circle-outline" slot="start" style="margin-right: 8px;"></ion-icon>
      Create Support Ticket
    </ion-title>
    <ion-buttons slot="end">
      <ion-button fill="clear" (click)="closeModal()">
        <ion-icon name="close-outline"></ion-icon>
      </ion-button>
    </ion-buttons>
  </ion-toolbar>
</ion-header>

<ion-content class="ion-padding">
  <!-- Step Indicator -->
  <div class="step-indicator">
    <div class="step" [class.active]="currentStep === 1" [class.completed]="currentStep > 1">
      <div class="step-number">1</div>
      <div class="step-label">Category</div>
    </div>
    <div class="step-line" [class.completed]="currentStep > 1"></div>
    <div class="step" [class.active]="currentStep === 2" [class.completed]="currentStep > 2">
      <div class="step-number">2</div>
      <div class="step-label">Severity</div>
    </div>
    <div class="step-line" [class.completed]="currentStep > 2"></div>
    <div class="step" [class.active]="currentStep === 3" [class.completed]="currentStep > 3">
      <div class="step-number">3</div>
      <div class="step-label">Details</div>
    </div>
    <div class="step-line" [class.completed]="currentStep > 3"></div>
    <div class="step" [class.active]="currentStep === 4" [class.completed]="currentStep > 4">
      <div class="step-number">4</div>
      <div class="step-label">Review</div>
    </div>
  </div>

  <!-- Step 1: Category Selection -->
  <div *ngIf="currentStep === 1" class="step-content">
    <h2>What type of issue are you experiencing?</h2>
    <p class="step-subtitle">This helps us route your ticket to the right team.</p>

    <ion-list>
      <ion-item [class.selected]="formData.category === category" 
        *ngFor="let category of categories" 
        (click)="selectCategory(category)"
        lines="none">
        <ion-icon [name]="getCategoryIcon(category)" slot="start" class="category-icon"></ion-icon>
        <ion-label>
          <h3>{{ getCategoryLabel(category) }}</h3>
          <p>{{ getCategoryDescription(category) }}</p>
        </ion-label>
        <ion-radio slot="end"></ion-radio>
      </ion-item>
    </ion-list>
  </div>

  <!-- Step 2: Severity Selection -->
  <div *ngIf="currentStep === 2" class="step-content">
    <h2>How would you rate the severity?</h2>
    <p class="step-subtitle">This helps us prioritize your issue.</p>

    <div class="severity-buttons">
      <ion-button 
        [color]="formData.severity === severity ? 'primary' : 'medium'"
        [fill]="formData.severity === severity ? 'solid' : 'outline'"
        *ngFor="let severity of severities"
        (click)="selectSeverity(severity)">
        <ion-icon [name]="getSeverityIcon(severity)" slot="start"></ion-icon>
        <div class="severity-content">
          <div>{{ getSeverityLabel(severity) }}</div>
        </div>
      </ion-button>
    </div>
  </div>

  <!-- Step 3: Description & Attachments -->
  <div *ngIf="currentStep === 3" class="step-content">
    <h2>Tell us about the issue</h2>
    <p class="step-subtitle">Provide as much detail as possible to help us resolve it faster.</p>

    <!-- Description -->
    <ion-item>
      <ion-label position="stacked">
        <span>Issue Description <span style="color: var(--ion-color-danger);">*</span></span>
      </ion-label>
      <ion-textarea [(ngModel)]="formData.description" 
        placeholder="Briefly summarize the issue, then provide detailed explanation..."
        autoGrow="true"
        rows="5"></ion-textarea>
    </ion-item>

    <!-- Show More / Show Less for Optional Fields -->
    <ion-button fill="clear" expand="block" (click)="showOptionalFields = !showOptionalFields">
      <ion-icon [name]="showOptionalFields ? 'chevron-up-outline' : 'chevron-down-outline'" slot="start"></ion-icon>
      {{ showOptionalFields ? 'Hide' : 'Show' }} Additional Details
    </ion-button>

    <!-- Optional Fields (Hidden by Default) -->
    <div *ngIf="showOptionalFields" class="optional-fields">
      <ion-item>
        <ion-label position="stacked">
          <ion-icon name="list-outline" slot="start"></ion-icon>
          Steps to Reproduce
        </ion-label>
        <ion-textarea [(ngModel)]="formData.reproducibilitySteps" 
          placeholder="1. First step&#10;2. Second step&#10;3. etc."
          autoGrow="true"
          rows="3"></ion-textarea>
      </ion-item>

      <ion-item>
        <ion-label position="stacked">
          <ion-icon name="checkmark-circle-outline" slot="start"></ion-icon>
          Expected Behavior
        </ion-label>
        <ion-textarea [(ngModel)]="formData.expectedBehavior" 
          placeholder="What should happen..."
          autoGrow="true"
          rows="2"></ion-textarea>
      </ion-item>

      <ion-item>
        <ion-label position="stacked">
          <ion-icon name="alert-circle-outline" slot="start"></ion-icon>
          Actual Behavior
        </ion-label>
        <ion-textarea [(ngModel)]="formData.actualBehavior" 
          placeholder="What actually happened..."
          autoGrow="true"
          rows="2"></ion-textarea>
      </ion-item>
    </div>

    <!-- File Upload Section -->
    <div class="attachments-section">
      <h4>Add Attachments (Optional)</h4>
      <p class="section-subtitle">Screenshots, error logs, and other files help us understand the issue better.</p>

      <ion-card color="light">
        <ion-card-content>
          <input type="file" #fileInput hidden (change)="onFileSelected($event)" multiple 
            accept="image/*,.pdf,.txt,.log,.json">
          <ion-button expand="block" fill="outline" (click)="fileInput.click()">
            <ion-icon name="cloud-upload-outline" slot="start"></ion-icon>
            Choose Files to Upload
          </ion-button>
          <p class="file-hint">Accepted: Images, PDF, TXT, LOG, JSON (max 10MB per file)</p>
        </ion-card-content>
      </ion-card>

      <!-- Uploaded Files List -->
      <div *ngIf="selectedFiles.length > 0" class="uploaded-files">
        <h5>Attached Files ({{ selectedFiles.length }})</h5>
        <ion-item *ngFor="let file of selectedFiles; let i = index">
          <ion-icon name="document-outline" slot="start" color="primary"></ion-icon>
          <ion-label>
            <h3>{{ file.name }}</h3>
            <p>{{ formatFileSize(file.size) }}</p>
          </ion-label>
          <ion-button fill="clear" slot="end" (click)="removeFile(i)">
            <ion-icon name="close-outline"></ion-icon>
          </ion-button>
        </ion-item>
      </div>
    </div>
  </div>

  <!-- Step 4: Review & Submit -->
  <div *ngIf="currentStep === 4" class="step-content">
    <h2>Review Your Ticket</h2>
    <p class="step-subtitle">Please review the details before submitting.</p>

    <ion-card>
      <ion-card-header>
        <ion-card-title>
          <ion-icon name="information-circle-outline" slot="start"></ion-icon>
          Category & Severity
        </ion-card-title>
      </ion-card-header>
      <ion-card-content>
        <ion-item lines="none" class="review-item">
          <ion-icon [name]="getCategoryIcon(formData.category)" slot="start" class="review-icon primary"></ion-icon>
          <ion-label>
            <div class="review-label">Category</div>
            <div class="review-value">{{ getCategoryLabel(formData.category) }}</div>
          </ion-label>
        </ion-item>
        <ion-item lines="none" class="review-item">
          <ion-icon [name]="getSeverityIcon(formData.severity)" slot="start" class="review-icon" 
            [ngClass]="{'critical': formData.severity === 'critical', 'high': formData.severity === 'high', 'medium': formData.severity === 'medium', 'low': formData.severity === 'low'}"></ion-icon>
          <ion-label>
            <div class="review-label">Severity</div>
            <div class="review-value">{{ getSeverityLabel(formData.severity) }}</div>
          </ion-label>
        </ion-item>
      </ion-card-content>
    </ion-card>

    <ion-card>
      <ion-card-header>
        <ion-card-title>
          <ion-icon name="document-text-outline" slot="start"></ion-icon>
          Issue Details
        </ion-card-title>
      </ion-card-header>
      <ion-card-content>
        <ion-item lines="none" class="review-item">
          <ion-icon name="sparkles-outline" slot="start" color="primary"></ion-icon>
          <ion-label>
            <div class="review-label">Summary</div>
            <div class="review-value">{{ formData.abstract }}</div>
          </ion-label>
        </ion-item>
        <ion-item lines="none" class="review-item">
          <ion-icon name="document-outline" slot="start" color="primary"></ion-icon>
          <ion-label>
            <div class="review-label">Description</div>
            <div class="review-value">{{ formData.description }}</div>
          </ion-label>
        </ion-item>
        <ion-item lines="none" class="review-item" *ngIf="formData.reproducibilitySteps">
          <ion-icon name="list-outline" slot="start" color="primary"></ion-icon>
          <ion-label>
            <div class="review-label">Reproduction Steps</div>
            <div class="review-value">{{ formData.reproducibilitySteps }}</div>
          </ion-label>
        </ion-item>
      </ion-card-content>
    </ion-card>

    <ion-card *ngIf="selectedFiles.length > 0">
      <ion-card-header>
        <ion-card-title>
          <ion-icon name="cloud-outline" slot="start"></ion-icon>
          Attachments
        </ion-card-title>
      </ion-card-header>
      <ion-card-content>
        <ion-item *ngFor="let file of selectedFiles" lines="none" class="review-item">
          <ion-icon name="document-outline" slot="start" color="primary"></ion-icon>
          <ion-label>
            <div class="review-value">{{ file.name }} ({{ formatFileSize(file.size) }})</div>
          </ion-label>
        </ion-item>
      </ion-card-content>
    </ion-card>

    <ion-card *ngIf="formData.systemContext" color="light">
      <ion-card-header>
        <ion-card-title>
          <ion-icon name="phone-portrait-outline" slot="start"></ion-icon>
          System Information
        </ion-card-title>
      </ion-card-header>
      <ion-card-content>
        <ion-item lines="none" class="review-item" *ngIf="formData.systemContext.browser">
          <ion-icon name="logo-chrome" slot="start" color="medium"></ion-icon>
          <ion-label><small>{{ formData.systemContext.browser }}</small></ion-label>
        </ion-item>
        <ion-item lines="none" class="review-item" *ngIf="formData.systemContext.os">
          <ion-icon name="desktop-outline" slot="start" color="medium"></ion-icon>
          <ion-label><small>{{ formData.systemContext.os }}</small></ion-label>
        </ion-item>
        <ion-item lines="none" class="review-item" *ngIf="formData.systemContext.deviceType">
          <ion-icon name="phone-portrait-outline" slot="start" color="medium"></ion-icon>
          <ion-label><small>{{ formData.systemContext.deviceType }}</small></ion-label>
        </ion-item>
      </ion-card-content>
    </ion-card>
  </div>

  <!-- Error & Success Messages -->
  <div class="feedback-container">
    <ion-text color="success" *ngIf="successMessage">
      <ion-icon name="checkmark-circle-outline" slot="start"></ion-icon>
      {{ successMessage }}
    </ion-text>
    <ion-text color="danger" *ngIf="errorMessage">
      <ion-icon name="warning-outline" slot="start"></ion-icon>
      {{ errorMessage }}
    </ion-text>
  </div>

  <!-- Navigation Buttons -->
  <div class="button-group">
    <ion-button expand="block" fill="outline" (click)="previousStep()" [disabled]="currentStep === 1">
      <ion-icon name="chevron-back-outline" slot="start"></ion-icon>
      Back
    </ion-button>
    <ion-button expand="block" fill="solid" color="primary" 
      *ngIf="currentStep < 4"
      (click)="nextStep()" 
      [disabled]="!isStepValid()">
      <ion-icon name="chevron-forward-outline" slot="end"></ion-icon>
      Next
    </ion-button>
    <ion-button expand="block" fill="solid" color="success" 
      *ngIf="currentStep === 4"
      (click)="submitTicket()"
      [disabled]="isSubmitting || !formData.abstract?.trim() || !formData.description?.trim()">
      <ng-container *ngIf="!isSubmitting">
        <ion-icon name="send-outline" slot="start"></ion-icon>
        Submit Ticket
      </ng-container>
      <ng-container *ngIf="isSubmitting">
        <ion-spinner name="crescent"></ion-spinner>
      </ng-container>
    </ion-button>
  </div>
</ion-content>